import type { TrustGraphView, TrustGraphViewEdge, TrustGraphViewNode } from '@uncain/shared';
import type { ElementDefinition } from 'cytoscape';

interface TrustGraphFilterOptions {
	readonly searchTerm: string;
	readonly showHiddenNodes: boolean;
	readonly riskOnly: boolean;
	readonly pinnedNodeId: string | null;
}

export interface FilteredTrustGraph {
	readonly nodes: readonly TrustGraphViewNode[];
	readonly edges: readonly TrustGraphViewEdge[];
	readonly elements: readonly ElementDefinition[];
	readonly selectedNode: TrustGraphViewNode | null;
}

export function filterTrustGraphView(
	view: TrustGraphView,
	options: TrustGraphFilterOptions,
): FilteredTrustGraph {
	const visibleNodeIds = collectVisibleNodeIds(view, options);
	const nodes = view.nodes.filter((node) => visibleNodeIds.has(node.id));
	const edges = view.edges.filter(
		(edge) => visibleNodeIds.has(edge.sourceId) && visibleNodeIds.has(edge.targetId),
	);

	return {
		nodes,
		edges,
		elements: toCytoscapeElements(nodes, edges),
		selectedNode: options.pinnedNodeId
			? (nodes.find((node) => node.id === options.pinnedNodeId) ?? null)
			: null,
	};
}

function collectVisibleNodeIds(
	view: TrustGraphView,
	options: TrustGraphFilterOptions,
): Set<string> {
	const defaultVisible = new Set(
		view.nodes
			.filter((node) => options.showHiddenNodes || !node.isHiddenByDefault)
			.map((node) => node.id),
	);

	if (options.riskOnly) {
		const riskyNodeIds = new Set(
			view.nodes
				.filter(
					(node) =>
						node.findingCount > 0 ||
						node.isQuarantined ||
						view.defaultFilters.focusNodeIds.includes(node.id) ||
						view.defaultFilters.highlightedNodeIds.includes(node.id),
				)
				.map((node) => node.id),
		);
		expandNeighbors(riskyNodeIds, view.edges);
		retainIntersection(defaultVisible, riskyNodeIds);
	}

	const search = options.searchTerm.trim().toLowerCase();
	if (search) {
		const matchedNodeIds = new Set(
			view.nodes
				.filter((node) => node.matchKeys.some((key) => key.toLowerCase().includes(search)))
				.map((node) => node.id),
		);
		expandNeighbors(matchedNodeIds, view.edges);
		retainIntersection(defaultVisible, matchedNodeIds);
	}

	if (options.pinnedNodeId) {
		defaultVisible.add(options.pinnedNodeId);
	}

	return defaultVisible;
}

/** Expand seed nodeIds by at most 1 hop using a BFS pass. */
function expandNeighbors(nodeIds: Set<string>, edges: readonly TrustGraphViewEdge[]): void {
	const seeds = [...nodeIds];
	for (const edge of edges) {
		if (seeds.includes(edge.sourceId)) {
			nodeIds.add(edge.targetId);
		}
		if (seeds.includes(edge.targetId)) {
			nodeIds.add(edge.sourceId);
		}
	}
}

function retainIntersection(target: Set<string>, allowed: ReadonlySet<string>): void {
	for (const value of [...target]) {
		if (!allowed.has(value)) {
			target.delete(value);
		}
	}
}

function toCytoscapeElements(
	nodes: readonly TrustGraphViewNode[],
	edges: readonly TrustGraphViewEdge[],
): readonly ElementDefinition[] {
	return [
		...nodes.map((node) => ({
			group: 'nodes' as const,
			data: {
				id: node.id,
				label: node.label,
				subtitle: node.subtitle ?? '',
				nodeType: node.nodeType,
				category: node.category,
				risk: node.risk,
				findingCount: node.findingCount,
				isQuarantined: node.isQuarantined,
				isReleasePath: node.isReleasePath,
			},
		})),
		...edges.map((edge) => ({
			group: 'edges' as const,
			data: {
				id: edge.id,
				source: edge.sourceId,
				target: edge.targetId,
				label: edge.label ?? edge.edgeType,
				risk: edge.risk,
				edgeType: edge.edgeType,
			},
		})),
	];
}
